import { useRef } from 'react'
import { Head, Link, router } from '@inertiajs/react'
import { Bot, Clock, FileCheck2, Plus, Search } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import WorkerPortalShell from '@/frontend/layouts/worker-first/WorkerPortalShell'
import { usePermissions } from '@/frontend/hooks/usePermissions'
import type { ScopeWorker } from '@/frontend/types/inertia'
import type { DigitalWorkerDetail } from '@/frontend/pages/Dashboard/DigitalWorkers/types'
import ProcessProgressBadge from './ProcessProgressBadge'
import StatusBadge from './StatusBadge'
import { EmptyState, FilterEmptyState } from '@/frontend/components/EmptyState'
import type { BusinessProcessSummary, StatusOption } from './types'

interface Pagination {
  current_page: number
  total_pages: number
  total_count: number
  per_page: number
}

interface Props {
  page_title: string
  processes: BusinessProcessSummary[]
  pagination: Pagination
  current_filters: { search: string; status: string }
  status_options: StatusOption[]
  scope_worker?: ScopeWorker
  // Ficha completa do agente em agent-scope: alimenta o FichaHeader dentro do
  // WorkerPortalShell (a moldura do portal). Ausente no modo org-level.
  scope_worker_header?: DigitalWorkerDetail
}

function navigate(filters: Props['current_filters'], page?: number, scopeWorker?: ScopeWorker) {
  // In agent scope the page lives on the canonical worker URL (C.2, SZ-1597);
  // filter/pagination visits stay there instead of round-tripping through the
  // legacy ?digital_worker_id= redirect.
  const basePath = scopeWorker
    ? `/dashboard/digital_workers/${scopeWorker.id}/processos`
    : '/dashboard/business_processes'
  router.get(basePath, {
    page: page || undefined,
    search: filters.search || undefined,
    status: filters.status || undefined,
  }, { preserveState: true })
}

function formatDate(value: string) {
  return new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short', timeStyle: 'short' }).format(new Date(value))
}

export default function BusinessProcessesIndex({ page_title, processes, pagination, current_filters, status_options, scope_worker, scope_worker_header }: Props) {
  const { can_manage_resources } = usePermissions()
  const hasFilters = current_filters.search || current_filters.status
  const searchInputRef = useRef<HTMLInputElement>(null)

  // Limpa filtros e zera o input (não-controlado, via ref) numa tacada só.
  const clearFilters = () => {
    if (searchInputRef.current) searchInputRef.current.value = ''
    navigate({ search: '', status: '' }, undefined, scope_worker)
  }

  const body = (
    <>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
        <div>
          {/* Título de página do Painel: 19/650 (escala do DS). */}
          <h1 className="text-[19px] font-semibold tracking-[-0.01em] text-foreground">{page_title}</h1>
          <p className="mt-1 text-[13px] text-muted-foreground">
            {scope_worker
              ? `Processos atribuídos a ${scope_worker.name}.`
              : 'Processos humanos transformados em instruções de execução para agentes.'}
          </p>
        </div>
        {can_manage_resources && (
          <Button asChild>
            <Link href="/dashboard/business_processes/new">
              <Plus className="size-4" />
              Novo processo
            </Link>
          </Button>
        )}
      </div>

      <div className="flex flex-col gap-2 md:flex-row">
        <div className="relative md:w-[18rem]">
          <Search className="absolute left-2.5 top-2.5 size-4 text-muted-foreground" />
          <Input
            ref={searchInputRef}
            placeholder="Buscar processos..."
            defaultValue={current_filters.search}
            onChange={(event) => navigate({ ...current_filters, search: event.target.value }, undefined, scope_worker)}
            className="pl-8"
          />
        </div>
        <Select value={current_filters.status || 'all'} onValueChange={(value) => navigate({ ...current_filters, status: value === 'all' ? '' : value }, undefined, scope_worker)}>
          <SelectTrigger className="md:w-[14rem]" aria-label="Filtrar processos">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos os processos</SelectItem>
            {status_options.map((option) => (
              <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {processes.length === 0 ? (
        hasFilters ? (
          <FilterEmptyState query={current_filters.search} onClear={clearFilters} />
        ) : (
          <EmptyState
            illustration="processes"
            title="Nenhum processo ainda"
            description={scope_worker
              ? `Crie um processo atribuído a ${scope_worker.name} ou veja todos os da organização.`
              : 'Crie um processo para preparar um agente para executá-lo.'}
          />
        )
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {processes.map((process) => (
            <Link
              key={process.id}
              href={`/dashboard/business_processes/${process.id}`}
              aria-label={process.name}
              className="group flex flex-col rounded-xl border border-border bg-card p-4 transition-colors duration-150 hover:border-input"
            >
              <div className="flex items-start justify-between gap-2">
                <h3 className="line-clamp-2 text-[14px] font-semibold leading-snug text-foreground group-hover:text-brand">{process.name}</h3>
                <div className="flex shrink-0 flex-wrap items-center justify-end gap-1.5">
                  <StatusBadge status={process.status} label={process.status_label} />
                  <ProcessProgressBadge progressKind={process.progress_kind} />
                </div>
              </div>
              <div className="mt-3 space-y-1.5 text-[13px] text-muted-foreground">
                <div className="flex items-center gap-1.5">
                  <Bot className="size-3.5 shrink-0" />
                  <span className="truncate">{process.digital_worker_name}</span>
                </div>
                <div className="flex items-center gap-1.5">
                  <Clock className="size-3.5 shrink-0" />
                  <span>Atualizado <span className="font-mono text-[11.5px]">{formatDate(process.updated_at)}</span></span>
                </div>
              </div>
              {process.has_published_skill && (
                <div className="mt-3">
                  <Badge variant="success" className="gap-1">
                    <FileCheck2 className="size-3" />
                    {process.status === 'published' ? 'Disponível ao agente' : 'Versão anterior disponível ao agente'}
                  </Badge>
                </div>
              )}
            </Link>
          ))}
        </div>
      )}

      {pagination.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <span className="text-[13px] tabular-nums text-muted-foreground">
            Página {pagination.current_page} de {pagination.total_pages} ({pagination.total_count} processos)
          </span>
          <div className="flex gap-2">
            {pagination.current_page > 1 && <Button variant="outline" size="sm" onClick={() => navigate(current_filters, pagination.current_page - 1, scope_worker)}>Anterior</Button>}
            {pagination.current_page < pagination.total_pages && <Button variant="outline" size="sm" onClick={() => navigate(current_filters, pagination.current_page + 1, scope_worker)}>Próxima</Button>}
          </div>
        </div>
      )}
    </>
  )

  return (
    <>
      <Head title={page_title} />
      {/* Em agent-scope a página vive dentro da moldura do portal (FichaHeader
          + WorkerPortalNav do WorkerPortalShell); no modo org-level segue seca
          num container simples — comportamento org-level intocado. */}
      {scope_worker_header ? (
        <WorkerPortalShell worker={scope_worker_header} section="processos">{body}</WorkerPortalShell>
      ) : (
        <div className="space-y-6">{body}</div>
      )}
    </>
  )
}
